// ---------------------------------------------------------------------------
// Types (shared by ShippingHubPage)
// ---------------------------------------------------------------------------

export interface ShippingCarrierRow {
  mv_shipping_provider_id: number;
  name: string;
  tracking_url: string | null;
  /** When false, outbound mark-shipped may omit tracking (hub-local carriers). */
  requires_tracking?: boolean;
}

export interface SkuQuantity {
  sku: string;
  quantity: number;
  boxed: number;
  remark: string | null;
  /** Matches packing slip / pre-pack: COALESCE(carton_number, 0). Omitted on older API payloads. */
  carton_key?: number;
}

export interface ShipmentRow {
  shipment_id: number;
  sku_quantities: SkuQuantity[] | null;
  date_shipped: string | null;
  carrier: string | null;
  tracking: string | null;
  ready_for_pickup: boolean | null;
  packing_slips: unknown;
  mfr_ship_by_date: string | null;
  batch_id: number;
  batch_location_id: number | null;
  manufacturer_sales_order_number: string | null;
  /** Distinct boxed cartons (serials with box_serial + bulk carton rows). */
  carton_count: number;
  /** Batch ship-to country (may be NONE); used with ship_to_state for international detection. */
  ship_to_country?: string | null;
  ship_to_state?: string | null;
}

export interface PackingSlipRow {
  sku: string;
  quantity: number;
  sales_order_number: string | null;
  box: number;
  batch_item_id: number | null;
  /** Megaventory sales order line id (`SalesOrderRowDetailID`). */
  sales_order_row_id: number | null;
  /** Cached line description from ingest when present. */
  mv_product_description?: string | null;
}

/** Matches **`public.shipment.packing_slip_pallet_allocation`** (version 1 JSON). */
export interface PackingSlipPalletAllocation {
  version: number;
  pallet_mode: boolean;
  pallet_by_box: Record<string, string>;
}

export interface PackingSlipRowsResponse {
  rows: PackingSlipRow[];
  pallet_allocation: PackingSlipPalletAllocation | null;
}

export interface PackingValue {
  salesOrderNumber: string;
  sku: string;
  box: number;
  quantity: number;
  pallet: string;
  batch_item_id: string;
  /** Filled by the API before pdf-it when Megaventory line text is resolved. */
  description?: string;
  /** Megaventory `SalesOrderRowDetailID`; forwarded to pdf-it for line matching. */
  salesOrderRowId?: number | string | null;
  /** Freight packing slip carton dims from `pack_list` (pdf-it optional fields). */
  packageLengthIn?: number;
  packageWidthIn?: number;
  packageHeightIn?: number;
  packageWeightLbs?: number;
}

export interface UpsLabelData {
  shipment_id: number;
  batch_id: number;
  location_id: number;
  ship_to_recipient: string | null;
  ship_to_attention: string | null;
  ship_to_address_line_1: string | null;
  ship_to_address_line_2: string | null;
  ship_to_address_line_3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip: string | null;
  ship_to_country: string | null;
  sales_order_number: string | null;
  po_number: string | null;
}

export interface LabelImage {
  id: number;
  tracking_no: string | null;
  img_base64: string | null;
  letter_pdf_base64?: string | null;
  label_4x6_pdf_base64?: string | null;
  format: string | null;
}

export interface ZplPrinter {
  name: string;
  description?: string;
  location?: string;
}

export interface UpsPackageInput {
  id: number;
  length: string;
  width: string;
  height: string;
  weight: string;
}

export interface ShipmentPackageDetail {
  box_number: number;
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  weight_lbs: number | null;
}

export function shipmentPackageDetailsAreComplete(packages: ShipmentPackageDetail[]): boolean {
  if (packages.length === 0) return false;
  return packages.every(
    (pkg) =>
      pkg.length_in != null &&
      pkg.width_in != null &&
      pkg.height_in != null &&
      pkg.weight_lbs != null &&
      pkg.length_in > 0 &&
      pkg.width_in > 0 &&
      pkg.height_in > 0 &&
      pkg.weight_lbs > 0,
  );
}

export interface TransferOrderItem {
  sku_id: string;
  description: string | null;
  quantity: number;
  received_quantity: number | null;
  status: string;
}

export interface TransferRow {
  id: number;
  transfer_number: string;
  status: string;
  destination_name: string | null;
  ship_by_date: string | null;
  shipped_at: string | null;
  shipping_method: string | null;
  notes: string | null;
  mv_transfer_document_number: string | null;
  total_lines: number;
  fulfilled_lines: number;
  box_count: number;
  is_overdue: boolean;
  sla_overdue: boolean;
  ready_for_pickup: boolean | null;
  carrier: string | null;
  tracking: string | null;
  items: TransferOrderItem[];
  latest_shipment: { carrier: string | null; tracking: string | null } | null;
  pending_pack_qty_request_count: number;
  has_inventory_shortfall: boolean;
}

export type OutboundUpsTarget =
  | { kind: "shipment"; row: ShipmentRow }
  | { kind: "transfer"; row: TransferRow };

export type OutboundPackingSlipTarget =
  | { kind: "shipment"; row: ShipmentRow }
  | { kind: "transfer"; row: TransferRow };

export type OutboundMarkShippedTarget =
  | { kind: "shipment"; row: ShipmentRow }
  | { kind: "transfer"; row: TransferRow };

export type ShippingUpsView = "create" | "result" | "void";

export type OutboundQueueEntry =
  | { kind: "shipment"; shipment: ShipmentRow }
  | { kind: "transfer"; transfer: TransferRow };
